import {
  getDefaultAmountBasedValue,
  getMaxAmountBasedTipOption,
  isUserInAmountBasedTipOptions,
} from '@/app/utils/abTesting/checkout-form';

type GetCalculatedTipParams = {
  currentTip: number;
  abGroup: string;
  amount: number;
  tipOptionsForLowDonation: number[];
  tipOptionsForHighDonation: number[];
  tipBreakpoint: number;
  previousAmount: number;
  defaultTipForAmountBelowBreakpoint: number;
  defaultTipForAmountAboveBreakpoint: number;
};

// if new options does not support current tip, value is changed to new min or nex max
// zero is supported but now mentioned in the options

export const getCalculatedTip = ({
  currentTip,
  amount,
  abGroup,
  tipBreakpoint,
  tipOptionsForHighDonation,
  tipOptionsForLowDonation,
  previousAmount,
  defaultTipForAmountBelowBreakpoint,
  defaultTipForAmountAboveBreakpoint,
}: GetCalculatedTipParams): [string, number] => {
  if (!amount || isNaN(amount)) {
    return ['tipPercentage', currentTip];
  }

  const group = isUserInAmountBasedTipOptions(abGroup);
  const matchedValue = amount <= getMaxAmountBasedTipOption();

  if (group && matchedValue) {
    const tip = getDefaultAmountBasedValue(amount);
    if (!tip) {
      return ['tipPercentage', currentTip];
    }

    return ['tipAmount', tip];
  }

  const tipOptions = amount > tipBreakpoint ? tipOptionsForHighDonation : tipOptionsForLowDonation;

  const highestPossibleTip = tipOptions[tipOptions.length - 1];
  const lowestPossibleTip = tipOptions[0];

  const isTipTooHigh = Number(amount) > tipBreakpoint && currentTip > highestPossibleTip;
  const isTipTooLow = Number(amount) <= tipBreakpoint && currentTip < lowestPossibleTip;
  const isTipSupported = tipOptions.includes(currentTip);

  const didAmountGoOverBreakpoint =
    Number.isFinite(previousAmount) && amount > tipBreakpoint && previousAmount <= tipBreakpoint;

  const didAmountGoBelowBreakpoint =
    Number.isFinite(previousAmount) && amount <= tipBreakpoint && previousAmount > tipBreakpoint;

  if (didAmountGoBelowBreakpoint) {
    return ['tipPercentage', defaultTipForAmountBelowBreakpoint];
  }

  if (didAmountGoOverBreakpoint) {
    return ['tipPercentage', defaultTipForAmountAboveBreakpoint];
  }

  if (currentTip === 0) {
    return ['tipPercentage', currentTip];
  }

  if (isTipTooHigh) {
    return ['tipPercentage', highestPossibleTip];
  }

  if (isTipTooLow) {
    return ['tipPercentage', lowestPossibleTip];
  }

  if (!isTipSupported) {
    return ['tipPercentage', 0];
  }

  return ['tipPercentage', currentTip];
};
